import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ChevronDown,
  ChevronRight,
  Download,
  CheckCircle,
  Users,
  DollarSign,
  TrendingDown,
} from "react-feather";

import { viewBatchApi, approveBatchApi, markPaidApi } from "../../../../Api/payrollApi";
import { viewOrganizationApi } from "../../../../Api/OrganizationApi";
import {
  checkStatusCodeSuccess,
  rendorBackButton,
  getLocalItem,
} from "../../../../Constant/common";
import { PAYROLL_STRINGS, PAYROLL_STATUS } from "../../../../Constant/HR/payrollConstants";
import { canEditBatch } from "../../../../Constant/HR/payrollCalculations";
import { generatePayslipPdf } from "../../../../Constant/HR/payslipPdfGenerator";
import PayrollStatusBadge from "../../../../BaseComponents/PayrollStatusBadge";
import BaseButton from "../../../../BaseComponents/BaseButton";
import "./BatchDetail.css";

const formatINR = (val) => {
  if (val == null || isNaN(val)) return "₹0";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

// ---------------------------------------------------------------------------
// Summary stat card
// ---------------------------------------------------------------------------
const SummaryCard = ({ icon: Icon, iconTheme = "indigo", label, value }) => (
  <div className="batch-detail-summary-card d-flex align-items-center gap-3">
    <div className={`batch-detail-summary-icon rounded summary-icon-${iconTheme}`}>
      <Icon size={18} strokeWidth={2} />
    </div>
    <div className="batch-detail-summary-content">
      <p className="batch-detail-summary-label">{label}</p>
      <p className="batch-detail-summary-value">{value}</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Expandable employee row
// ---------------------------------------------------------------------------
const EmployeeRow = ({ record, isExpanded, onToggle, batchMonth, orgName, orgAddress }) => {
  const name = record?.employeeName || record?.name || "Employee";
  const employeeId = record?.employeeId || "";
  const designation = record?.designation || "";
  const department = record?.department || "";
  const grossSalary = Number(record?.grossSalary ?? record?.gross_salary ?? record?.gross ?? 0);
  const netSalary = Number(record?.netSalary ?? record?.net_salary ?? record?.net ?? 0);
  const variableAmount = Number(
    record?.variable_amount ??
      record?.variableAmount ??
      record?.overtime_amount ??
      record?.overtimeAmount ??
      0,
  );
  const pfEmployer = Number(record?.pfEmployer ?? record?.pf_employer ?? 0);

  // Normalize earnings (fixed components only)
  let rawEarnings = record?.earnings;
  if (typeof rawEarnings === "string") {
    try {
      rawEarnings = JSON.parse(rawEarnings);
    } catch {
      // Fallback if rawEarnings is not a valid JSON string
    }
  }
  let earnings = Array.isArray(rawEarnings)
    ? rawEarnings
        .filter((e) => !/variable|overtime/i.test(e.component_name || e.name || ""))
        .map((e) => ({
          name: e.component_name || e.name || "Earning",
          amount: Number(e.amount ?? 0),
        }))
    : [];

  // Normalize deductions (filter out employer share from rows)
  let rawDeductions = record?.deductions;
  if (typeof rawDeductions === "string") {
    try {
      rawDeductions = JSON.parse(rawDeductions);
    } catch {
      // Fallback if rawDeductions is not a valid JSON string
    }
  }
  let deductions = (Array.isArray(rawDeductions) ? rawDeductions : [])
    .filter((d) => {
      const compName = (d.component_name || d.name || "").toLowerCase();
      return !compName.includes("employer") && !d.isEmployer && !d.is_employer;
    })
    .map((d) => ({
      name: d.component_name || d.name || "Deduction",
      amount: Number(d.amount ?? 0),
    }));

  const calcDeductions = deductions.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const totalDeductions =
    calcDeductions > 0
      ? calcDeductions
      : Number(
          record?.total_deductions ??
            record?.totalDeductions ??
            Math.max(0, grossSalary - netSalary),
        );

  const initials =
    name
      .split(" ")
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";

  const handleDownload = (e) => {
    e.stopPropagation();
    generatePayslipPdf({
      companyName: record?.companyName || orgName || "Your Company",
      companyAddress: record?.companyAddress || orgAddress || "",
      employeeName: name,
      employeeId,
      designation,
      department,
      payrollMonth: record?.payrollMonth || batchMonth || "Payroll",
      earnings:
        variableAmount > 0
          ? [...earnings, { name: "Variable Pay", amount: variableAmount }]
          : earnings,
      deductions,
      grossSalary,
      totalDeductions,
      netSalary,
      employerPf: pfEmployer,
      variable_amount: variableAmount,
      pfEmployee: record?.pfEmployee ?? record?.pf_employee,
      pf: record?.pf ?? record?.pf_employee,
      esic: record?.esic ?? record?.esic_amount,
      pt: record?.pt ?? record?.pt_amount,
      tds: record?.tds ?? record?.tds_amount,
    });
  };

  return (
    <div
      className={`employee-row glass rounded-2xl animate-slide-up ${isExpanded ? "expanded" : ""}`}
    >
      {/* ── Row header ── */}
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} details for ${name}`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="employee-row-header d-flex align-items-center gap-2"
      >
        {/* Expand chevron */}
        <div className="employee-row-chevron text-muted flex-shrink-0 d-flex align-items-center">
          {isExpanded ? (
            <ChevronDown size={15} strokeWidth={2} />
          ) : (
            <ChevronRight size={15} strokeWidth={2} />
          )}
        </div>

        {/* Avatar */}
        <div className="employee-row-avatar text-white fw-700">{initials}</div>

        {/* Name + ID */}
        <div className="employee-row-name-col">
          <p className="employee-row-name">{name}</p>
          <p className="employee-row-meta">
            {employeeId}
            {designation ? ` · ${designation}` : ""}
          </p>
        </div>

        {/* Department */}
        {department && (
          <div className="employee-row-dept">
            <p className="employee-row-dept-text">{department}</p>
          </div>
        )}

        {/* Gross */}
        <div className="employee-row-stat">
          <p className="employee-row-stat-label">Gross</p>
          <p className="employee-row-stat-value">{formatINR(grossSalary)}</p>
        </div>

        {/* Net */}
        <div className="employee-row-stat">
          <p className="employee-row-stat-label">Net</p>
          <p className="employee-row-stat-value net">{formatINR(netSalary)}</p>
        </div>

        {/* Download */}
        <div className="employee-row-download">
          <BaseButton
            color="secondary"
            onClick={handleDownload}
            aria-label={`Download payslip for ${name}`}
          >
            <Download size={13} aria-hidden="true" />
            {PAYROLL_STRINGS.BTN_DOWNLOAD_PAYSLIP}
          </BaseButton>
        </div>
      </div>

      {/* ── Expanded breakdown ── */}
      {isExpanded && (
        <div className="employee-breakdown animate-slide-down">
          {/* Earnings */}
          <div className="earnings-section">
            <p className="earnings-section-title">Earnings</p>
            {earnings.length === 0 ? (
              <p className="empty-breakdown-text">No earnings data</p>
            ) : (
              <table className="earnings-table">
                <tbody>
                  {earnings.map((e, i) => (
                    <tr key={i}>
                      <td>{e.name}</td>
                      <td>{formatINR(e.amount)}</td>
                    </tr>
                  ))}
                  <tr className="breakdown-total-row">
                    <td>Gross Salary</td>
                    <td>{formatINR(grossSalary)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Deductions */}
          <div className="deductions-section">
            <p className="deductions-section-title">Deductions</p>
            {deductions.length === 0 ? (
              <p className="empty-breakdown-text">No deductions</p>
            ) : (
              <table className="deductions-table">
                <tbody>
                  {deductions.map((d, i) => (
                    <tr key={i}>
                      <td>{d.name}</td>
                      <td>{formatINR(d.amount)}</td>
                    </tr>
                  ))}
                  <tr className="breakdown-total-row">
                    <td>Total Deductions</td>
                    <td>{formatINR(totalDeductions)}</td>
                  </tr>
                </tbody>
              </table>
            )}

            {/* PF Employer Note */}
            {pfEmployer > 0 && (
              <div className="employer-pf-note">
                <span className="employer-pf-note-label">Note:</span>
                <span>
                  Employer PF Contribution (informational): <strong>{formatINR(pfEmployer)}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Unified Variable Pay & Net Salary Full-Width Card */}
          <div className="variable-net-banner">
            {/* Variable Pay on the Left */}
            {variableAmount > 0 ? (
              <div className="variable-pay-left">
                <div className="variable-pay-badge-icon">+</div>
                <div>
                  <span className="variable-pay-label">Variable Pay / Overtime</span>
                  <span className="variable-pay-amount">{formatINR(variableAmount)}</span>
                </div>
              </div>
            ) : (
              <div className="salary-mini-breakdown">
                <span className="salary-mini-text">
                  Gross:{" "}
                  <strong className="salary-mini-strong-primary">{formatINR(grossSalary)}</strong> ·
                  Deductions:{" "}
                  <strong className="salary-mini-strong-rose">{formatINR(totalDeductions)}</strong>
                </span>
              </div>
            )}

            {/* Net Salary on the Right */}
            <div className="net-salary-right">
              <div className="net-salary-right-content">
                <span className="net-salary-right-label">Net Salary</span>
                <span className="net-salary-right-amount">{formatINR(netSalary)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const BatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isApproving, setIsApproving] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const [orgName, setOrgName] = useState("");
  const [orgAddress, setOrgAddress] = useState("");

  useEffect(() => {
    const orgId = getLocalItem("organizationId");
    if (orgId) {
      viewOrganizationApi(orgId)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const org = resp?.data?.organization;
            if (org?.organization_name) setOrgName(org.organization_name);
            if (org?.description) setOrgAddress(org.description);
          }
        })
        .catch(() => {});
    }
  }, []);

  const fetchBatch = useCallback(() => {
    if (!id) return;
    setIsLoading(true);
    viewBatchApi(id)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setBatch(resp?.data ?? null);
        } else {
          toast.error(resp?.message || PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || PAYROLL_STRINGS.ERROR_GENERIC);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchBatch();
    });
  }, [fetchBatch]);

  const isEditable = canEditBatch(batch);
  const isPaid = batch?.status === PAYROLL_STATUS.PAID;
  // const isApproved = batch?.status === PAYROLL_STATUS.APPROVED || isPaid;
  const employees = Array.isArray(batch?.employees) ? batch.employees : [];
  const batchMonth = batch?.payrollMonth || `${batch?.month ?? ""}/${batch?.year ?? ""}`;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  };

  const handleApprove = () => {
    if (!id || !isEditable) return;
    setIsApproving(true);
    approveBatchApi(id)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setBatch((prev) => (prev ? { ...prev, status: PAYROLL_STATUS.APPROVED } : prev));
          toast.success(PAYROLL_STRINGS.SUCCESS_BATCH_APPROVED);
        } else {
          toast.error(resp?.message || PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || PAYROLL_STRINGS.ERROR_GENERIC);
      })
      .finally(() => setIsApproving(false));
  };

  const handleMarkPaid = () => {
    if (!id || !isEditable) return;
    setIsMarkingPaid(true);
    markPaidApi(id)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setBatch((prev) => (prev ? { ...prev, status: PAYROLL_STATUS.PAID } : prev));
          toast.success(PAYROLL_STRINGS.SUCCESS_BATCH_PAID);
        } else {
          toast.error(resp?.message || PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || PAYROLL_STRINGS.ERROR_GENERIC);
      })
      .finally(() => setIsMarkingPaid(false));
  };

  if (isLoading) {
    return (
      <div className="animate-fade-in d-flex flex-column gap-3">
        <div className="batch-detail-header glass rounded-2xl">
          <p className="payroll-loading-text text-center">Loading batch details…</p>
        </div>
      </div>
    );
  }

  if (!batch && !isLoading) {
    return (
      <div className="animate-fade-in d-flex flex-column gap-3">
        {rendorBackButton(() => navigate("/payrollHistory"))}
        <div className="batch-detail-header glass rounded-2xl">
          <p className="payroll-loading-text text-center">Batch not found.</p>
        </div>
      </div>
    );
  }

  const summary = batch?.summary || {};
  const totalGross = summary.totalGross ?? batch?.totalGross ?? 0;
  const totalDeductions = summary.totalDeductions ?? batch?.totalDeductions ?? 0;
  const totalNet = summary.totalNet ?? batch?.totalNet ?? 0;
  const totalEmployees = summary.totalEmployees ?? employees.length ?? 0;

  return (
    <div className="animate-fade-in d-flex flex-column gap-3">
      {/* Back */}
      <div className="animate-slide-down">
        {rendorBackButton(() => navigate("/payrollHistory"))}
      </div>

      {/* ── Batch header card ── */}
      <div className="batch-detail-header glass rounded-2xl animate-slide-up">
        {/* Title row */}
        <div className="batch-detail-header-title">
          <div>
            <h1 className="batch-detail-title">{batchMonth}</h1>
          </div>
          <div className="batch-detail-header-actions">
            <PayrollStatusBadge status={batch?.status} />
            {isEditable && (
              <>
                {batch?.status === PAYROLL_STATUS.DRAFT && (
                  <BaseButton
                    color="primary"
                    onClick={handleApprove}
                    loader={isApproving}
                    disabled={isApproving}
                    aria-label="Approve payroll batch"
                  >
                    {isApproving ? PAYROLL_STRINGS.LOADING_APPROVING : PAYROLL_STRINGS.BTN_APPROVE}
                  </BaseButton>
                )}
                {batch?.status === PAYROLL_STATUS.APPROVED && (
                  <BaseButton
                    color="success"
                    onClick={handleMarkPaid}
                    loader={isMarkingPaid}
                    disabled={isMarkingPaid}
                    aria-label="Mark payroll batch as paid"
                  >
                    {isMarkingPaid
                      ? PAYROLL_STRINGS.LOADING_PROCESSING
                      : PAYROLL_STRINGS.BTN_MARK_PAID}
                  </BaseButton>
                )}
              </>
            )}
          </div>
        </div>

        {/* Paid notice */}
        {isPaid && (
          <div className="batch-detail-paid-notice animate-fade-in" role="status">
            <CheckCircle size={15} className="batch-detail-paid-notice-icon" />
            <span className="batch-detail-paid-notice-text">
              {PAYROLL_STRINGS.ERROR_BATCH_PAID_LOCKED}
            </span>
          </div>
        )}

        {/* Summary cards */}
        <div className="batch-detail-summary-grid">
          <SummaryCard icon={Users} iconTheme="indigo" label="Employees" value={totalEmployees} />
          <SummaryCard
            icon={DollarSign}
            iconTheme="emerald"
            label="Total Gross"
            value={formatINR(totalGross)}
          />
          <SummaryCard
            icon={TrendingDown}
            iconTheme="rose"
            label="Total Deductions"
            value={formatINR(totalDeductions)}
          />
          <SummaryCard
            icon={DollarSign}
            iconTheme="indigo"
            label="Total Net"
            value={formatINR(totalNet)}
          />
        </div>
      </div>

      {/* ── Employee breakdown ── */}
      <div className="batch-detail-employees-section">
        <h2 className="batch-detail-section-title">Employee Breakdown</h2>

        {employees.length === 0 ? (
          <div className="payroll-empty-state glass rounded-2xl animate-fade-in">
            No employee salary data available for this batch.
          </div>
        ) : (
          <div className="stagger d-flex flex-column gap-2">
            {employees.map((record, index) => {
              const rowId = record?.staffId ?? record?.id ?? index;
              return (
                <EmployeeRow
                  key={rowId}
                  record={record}
                  index={index}
                  isExpanded={expandedRows.has(rowId)}
                  onToggle={() => toggleRow(rowId)}
                  batchMonth={batchMonth}
                  orgName={orgName}
                  orgAddress={orgAddress}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom back */}
      <div className="pb-1">{rendorBackButton(() => navigate("/payrollHistory"))}</div>
    </div>
  );
};

export default BatchDetail;
